import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Avatar } from '../components/Avatar'
import ReactMarkdown from 'react-markdown'
import {
  buehneWaehlen,
  fetchIntroChatHistoryHandy,
  fetchIntroState,
  gebeSchaufensterAb,
  fetchShowcases,
  openIntroChat,
  sendIntroChatMessage,
  sendIntroCommand,
  transcribeIntro,
  triggerIntro,
  type IntroChatNachricht,
  type IntroKamerakontext,
  type IntroZustand,
} from '../lib/api'
import { useIntroFoto } from '../components/IntroFoto'
import { PoweredBy } from '../components/PoweredBy'
import { streamChatEvents } from '../lib/sse'

/** Wie oft das Handy nachsieht, was der Bildschirm gerade tut. */
const POLL_MS = 1000
/** So lange hält das Handy die eigene Markierung, bis der Bildschirm sie bestätigt. */
const MARKIERUNG_MS = 4000
/** Längste Aufnahme — danach hört das Handy von selbst auf zuzuhören. */
const AUFNAHME_MAX_MS = 45_000

interface Box {
  id: string
  titel: string
}

/**
 * `/intro/go/<token>`: die URL auf dem Intro-Tag — und die Fernbedienung für
 * den Bildschirm mit `/intro`.
 *
 * Das Öffnen allein startet nichts: Erst der Knopf „Intro starten" löst aus.
 * Danach zeigt das Handy dieselben Boxen wie der Bildschirm (eine je
 * Showcase); eine antippen, und K4ia erzählt dort dazu. Was sie gerade
 * spricht, lässt sich hier anhalten, fortsetzen und stoppen.
 *
 * „Zum Chat" (oder „Sprechen") öffnet das Gespräch: Getippt oder gesprochen
 * wird am Handy, zu sehen und zu hören ist K4ia am Bildschirm. Gesprochenes
 * macht ElevenLabs zu Text — aufgenommen wird nur zwischen Tippen und Tippen,
 * und nichts davon wird gespeichert.
 */
export function IntroRemotePage({
  token,
  modus = 'intro',
  onFertig,
}: {
  token: string
  /**
   * `schaufenster`: Das Token ist das Steuer-Token aus dem Scan des QR-Codes
   * (SchaufensterRemotePage). Es gibt kein „Intro starten“ — K4ia begrüßt
   * schon beim Scan —, und wer fertig ist, gibt das Fenster frei.
   */
  modus?: 'intro' | 'schaufenster'
  /** Schaufenster: Steuerung abgegeben oder abgelaufen. */
  onFertig?: (grund: 'abgegeben' | 'abgelaufen') => void
}) {
  useState(() => buehneWaehlen(modus))
  const schaufenster = modus === 'schaufenster'
  /** Schaufenster: Hat der Bildschirm unseren Besuch schon angenommen? Erst danach heißt „wartet“: vorbei. */
  const angenommen = useRef(false)
  const [zustand, setZustand] = useState<IntroZustand | null>(null)
  const [boxen, setBoxen] = useState<Box[]>([])
  const [startet, setStartet] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  /** Sofort markiert, bevor der Bildschirm es meldet — sonst wirkt das Tippen eine Sekunde lang tot. */
  const [gewaehlt, setGewaehlt] = useState<string | null>(null)
  const gewaehltRef = useRef<string | null>(null)
  const gewaehltAm = useRef(0)
  const aufnahme = useAufnahme()

  useEffect(() => {
    void fetchShowcases()
      .then((r) => setBoxen(r.showcases.map((s) => ({ id: s.id, titel: s.title }))))
      .catch(() => undefined)
  }, [])

  useEffect(() => {
    let aktiv = true
    const takt = async () => {
      try {
        const neu = await fetchIntroState()
        if (!aktiv) return
        setZustand(neu)
        if (schaufenster) {
          // Zurück im Warten, nachdem der Besuch lief: Zeit um (oder anderswo beendet).
          if (neu.bildschirm.phase !== 'wartet') angenommen.current = true
          else if (angenommen.current) onFertig?.('abgelaufen')
        }
        if (neu.bildschirm.phase !== 'wartet') setStartet(false)
        // Die eigene Markierung gilt, bis der Bildschirm die Box meldet — sonst
        // ginge sie beim nächsten Nachfragen aus, weil er noch „menue" sagt.
        // Kommt die Bestätigung nicht, gilt nach ein paar Sekunden wieder er.
        const gewaehltAlt = Date.now() - gewaehltAm.current > MARKIERUNG_MS
        if (neu.bildschirm.thema === gewaehltRef.current || gewaehltAlt) {
          gewaehltRef.current = null
          setGewaehlt(null)
        }
      } catch {
        // Netz kurz weg: beim nächsten Durchlauf wieder
      }
    }
    void takt()
    const id = setInterval(() => void takt(), POLL_MS)
    return () => {
      aktiv = false
      clearInterval(id)
    }
  }, [])

  const phase = zustand?.bildschirm.phase
  const chat = useIntroChat(token, phase === 'chat')

  async function starten() {
    setFehler(null)
    setStartet(true)
    try {
      await triggerIntro(token)
    } catch {
      setStartet(false)
      setFehler('Das hat nicht geklappt. Halte das Handy bitte noch einmal an das Tag.')
    }
  }

  async function waehlen(id: string) {
    gewaehltRef.current = id
    gewaehltAm.current = Date.now()
    setGewaehlt(id)
    await sendIntroCommand(token, { art: 'thema', thema: id }).catch(() => undefined)
  }

  const befehl = (art: 'pause' | 'weiter' | 'stopp' | 'boxen' | 'ende') =>
    void sendIntroCommand(token, { art }).catch(() => undefined)

  async function chatOeffnen() {
    setFehler(null)
    try {
      await openIntroChat(token)
    } catch {
      setFehler('Der Chat ließ sich nicht öffnen. Versuch es bitte noch einmal.')
    }
  }

  /** Mikrofon: erstes Tippen hört zu, zweites schickt das Gesagte als Frage in den Chat. */
  async function mikrofon() {
    setFehler(null)
    if (aufnahme.status === 'hoert') {
      const blob = await aufnahme.stoppen()
      if (!blob) return
      aufnahme.setStatus('versteht')
      try {
        const text = await transcribeIntro(token, blob)
        if (!text) {
          setFehler('Ich habe nichts verstanden — tipp noch einmal und sprich etwas lauter.')
          return
        }
        if (phase !== 'chat') await chatOeffnen()
        await chat.fragen(text)
      } catch (e) {
        setFehler(
          e instanceof Error ? e.message : 'Die Spracherkennung ist gerade nicht erreichbar.',
        )
      } finally {
        aufnahme.setStatus('bereit')
      }
      return
    }
    try {
      await aufnahme.starten()
    } catch {
      setFehler('Ohne Mikrofon geht es nicht — erlaube es bitte in der Browser-Nachfrage.')
    }
  }

  if (!zustand) {
    return (
      <Rahmen avatar="thinking">
        <p className="text-xl">Einen Moment …</p>
      </Rahmen>
    )
  }

  if (!zustand.verbunden) {
    return schaufenster ? (
      <Rahmen avatar="idle">
        <p className="text-xl">Das Schaufenster ist gerade nicht erreichbar.</p>
        <p className="text-text-muted">Versuch es bitte gleich noch einmal.</p>
      </Rahmen>
    ) : (
      <Rahmen avatar="idle">
        <p className="text-xl">Der Bildschirm ist nicht verbunden.</p>
        <p className="text-text-muted">
          Öffne dort <code className="font-mono">/intro</code> und klicke einmal hinein.
        </p>
      </Rahmen>
    )
  }

  /** Schaufenster: das Fenster sofort freigeben, damit der Nächste darf. */
  const freigeben = () => {
    void gebeSchaufensterAb(token)
      .catch(() => undefined)
      .finally(() => onFertig?.('abgegeben'))
  }
  const ende = schaufenster
    ? {
        onEnde: freigeben,
        label: 'Fertig — Schaufenster freigeben',
        frage: 'Schaufenster freigeben? Dann kann die nächste Person übernehmen.',
      }
    : undefined

  const { spricht, pausiert } = zustand.bildschirm
  const steuerung = spricht ? <Steuerung pausiert={pausiert} onBefehl={befehl} /> : null
  const mikrofonKnopf = <MikrofonKnopf status={aufnahme.status} onClick={() => void mikrofon()} />
  const fehlerZeile = fehler && (
    <p className="text-center text-sm text-text-muted" role="alert">
      {fehler}
    </p>
  )

  if (phase === 'wartet' && schaufenster) {
    // Gerade erst gescannt: Der Bildschirm nimmt den Besuch in der nächsten Sekunde an.
    return (
      <Rahmen avatar="speaking">
        <p className="text-xl">Schau ins Schaufenster — K4ia begrüßt dich gleich.</p>
      </Rahmen>
    )
  }

  if (phase === 'wartet') {
    return (
      <Rahmen avatar={fehler ? 'error' : 'idle'}>
        <p className="text-xl">Hallo! Ich bin K4ia.</p>
        <button
          onClick={() => void starten()}
          disabled={startet}
          className="btn-primary w-full rounded-2xl px-8 py-5 text-xl font-semibold"
        >
          {startet ? 'Startet …' : 'Intro starten'}
        </button>
        {fehlerZeile}
      </Rahmen>
    )
  }

  if (phase === 'intro') {
    return (
      <Rahmen avatar="speaking">
        <p className="text-xl">Schau auf den Bildschirm — K4ia stellt sich vor.</p>
        {steuerung}
      </Rahmen>
    )
  }

  if (phase === 'chat') {
    return (
      <ChatFernbedienung
        chat={chat}
        token={token}
        titelVon={new Map(boxen.map((b) => [b.id, b.titel]))}
        steuerung={steuerung}
        mikrofonKnopf={mikrofonKnopf}
        fehlerZeile={fehlerZeile}
        spricht={spricht && !pausiert}
        onBoxen={() => befehl('boxen')}
        onEnde={() => befehl('ende')}
        freigabe={ende}
      />
    )
  }

  const aktiv = gewaehlt ?? (phase === 'thema' ? zustand.bildschirm.thema : null)
  const aktivTitel = boxen.find((b) => b.id === aktiv)?.titel
  return (
    <main className="mx-auto flex min-h-[calc(100dvh_-_var(--buehne-leiste,0px))] max-w-md flex-col gap-5 bg-bg p-5">
      <header className="flex items-center gap-3">
        <Avatar state={aktiv === null ? 'idle' : 'speaking'} size={56} showLabel={false} />
        <p className="text-lg" aria-live="polite">
          {aktiv === null ? 'Wozu soll K4ia erzählen?' : `K4ia erzählt: ${aktivTitel ?? '…'}`}
        </p>
      </header>

      {steuerung}

      <ul className="flex flex-1 flex-wrap content-start justify-center gap-3">
        {boxen.map((box) => (
          <li
            key={box.id}
            // Zwei nebeneinander, groß genug für lange Titel; geht die Anzahl
            // nicht auf, steht die letzte Box mittig statt links.
            className="w-[calc((100%-0.75rem)/2)]"
          >
            <button
              onClick={() => void waehlen(box.id)}
              aria-pressed={aktiv === box.id}
              className={`flex h-24 w-full items-center justify-center rounded-2xl border-2 p-3 text-center text-lg font-semibold leading-tight [hyphens:auto] transition active:scale-95 ${
                aktiv === box.id
                  ? 'border-[var(--color-accent)] bg-bg-elevated shadow-[0_0_24px_var(--color-accent)]'
                  : 'border-border bg-bg-elevated'
              }`}
            >
              {box.titel}
            </button>
          </li>
        ))}
      </ul>

      {fehlerZeile}
      <div className="flex gap-3">
        {mikrofonKnopf}
        <button
          onClick={() => void chatOeffnen()}
          className="flex-1 rounded-xl border border-border px-5 py-3 font-semibold"
        >
          Zum Chat
        </button>
      </div>
      {/* Beendet nur, was K4ia gerade erzählt — die Boxen bleiben. */}
      <button
        onClick={() => befehl('stopp')}
        // Schon frei, sobald eine Box angetippt ist — die Meldung des
        // Bildschirms, dass er spricht, kommt erst ein, zwei Sekunden später.
        disabled={!spricht && aktiv === null}
        className="rounded-xl border border-border px-5 py-3 font-semibold disabled:opacity-40"
      >
        <span aria-hidden>■ </span>Ansage beenden
      </button>
      <VorfuehrungBeenden {...(ende ?? { onEnde: () => befehl('ende') })} />
    </main>
  )
}

/**
 * Alles zurück auf Anfang: Bildschirm wartet wieder aufs Intro, der Chat ist
 * weg. Klein und mit Rückfrage — mitten in einer Führung aus Versehen
 * angetippt, wäre es teuer.
 */
function VorfuehrungBeenden({
  onEnde,
  label = 'Vorführung beenden',
  frage = 'Vorführung beenden? Der Bildschirm geht zurück zum Anfang.',
}: {
  onEnde: () => void
  label?: string
  frage?: string
}) {
  return (
    <button
      onClick={() => {
        if (window.confirm(frage)) onEnde()
      }}
      className="py-2 text-sm text-text-muted underline decoration-dotted underline-offset-4"
    >
      {label}
    </button>
  )
}

/** Wie oft das Handy im Chat den Verlauf nachliest (wie der Bildschirm). */
const CHAT_POLL_MS = 1500

/**
 * Der Intro-Chat am Handy. Nicht über den Chat-Store: Der gehört zum eigenen
 * Gespräch des Besuchers (Sitzungs-Cookie), und das soll nicht auf dem
 * Bildschirm im Raum landen. Hier gehen die Fragen über die Intro-Route mit
 * dem Tag-Token, und der Verlauf kommt von dort, wo auch der Bildschirm liest.
 */
function useIntroChat(token: string, aktiv: boolean) {
  const [verlauf, setVerlauf] = useState<IntroChatNachricht[]>([])
  /** Die Frage, deren Antwort gerade hereinkommt — bis sie im Verlauf steht. */
  const [laufend, setLaufend] = useState<{
    frage: string
    antwort: string
    foto?: string
  } | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    if (!aktiv) return
    // Beim Öffnen gilt: neuer, leerer Chat — nicht kurz noch der vorige.
    setVerlauf([])
    let an = true
    let zuletzt = ''
    const lesen = () =>
      void fetchIntroChatHistoryHandy(token)
        .then((r) => {
          // Nur übernehmen, was sich geändert hat: Jede Sekunde ein neuer
          // Verlauf hieß jede Sekunde neu rendern und neu scrollen — am Handy
          // sprang die Seite beim Tippen auf und ab (Flo, 10.10.).
          const jetzt = JSON.stringify(r.nachrichten)
          if (an && jetzt !== zuletzt) {
            zuletzt = jetzt
            setVerlauf(r.nachrichten)
          }
        })
        .catch(() => undefined)
    lesen()
    const id = setInterval(lesen, CHAT_POLL_MS)
    return () => {
      an = false
      clearInterval(id)
    }
  }, [aktiv, token])

  async function fragen(frage: string, kontext: IntroKamerakontext = {}) {
    setFehler(null)
    setLaufend({ frage, antwort: '', foto: kontext.imageDataUrl })
    try {
      const res = await sendIntroChatMessage(token, frage, kontext)
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string }
        throw new Error(body.error ?? body.message ?? `HTTP ${res.status}`)
      }
      for await (const event of streamChatEvents(res)) {
        if (event.type === 'token') {
          setLaufend((l) => (l ? { ...l, antwort: l.antwort + event.delta } : l))
        }
      }
      const { nachrichten } = await fetchIntroChatHistoryHandy(token)
      setVerlauf(nachrichten)
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Die Frage kam nicht an.')
    } finally {
      setLaufend(null)
    }
  }

  return { verlauf, laufend, fehler, fragen }
}

type IntroChat = ReturnType<typeof useIntroChat>

/** Der Chat am Handy: dasselbe Gespräch, das der Bildschirm groß zeigt und spricht. */
function ChatFernbedienung({
  chat,
  token,
  titelVon,
  steuerung,
  mikrofonKnopf,
  fehlerZeile,
  spricht,
  onBoxen,
  onEnde,
  freigabe,
}: {
  chat: IntroChat
  token: string
  titelVon: Map<string, string>
  steuerung: ReactNode
  mikrofonKnopf: ReactNode
  fehlerZeile: ReactNode
  spricht: boolean
  onBoxen: () => void
  onEnde: () => void
  /** Schaufenster: „Fenster freigeben“ statt „Vorführung beenden“. */
  freigabe?: { onEnde: () => void; label: string; frage: string }
}) {
  const [text, setText] = useState('')
  /** Ein Foto aufnehmen oder wählen: Der Bildschirm zeigt es, K4ia erkennt den Showcase oder beschreibt es. */
  const foto = useIntroFoto({ token, titelVon, onFrage: chat.fragen })
  const textRef = useRef(text)
  textRef.current = text
  const liste = useRef<HTMLElement | null>(null)
  const { verlauf, laufend } = chat
  const hoehe = useSichtbareHoehe()

  // Ans Ende scrollen — aber nur die Liste selbst und nur, wenn etwas dazukam.
  // `scrollIntoView` scrollte auch die Seite dahinter mit; zusammen mit der
  // Tastatur, die die Eingabe ins Bild schiebt, sprang alles hin und her.
  const stand = `${verlauf.length}:${verlauf[verlauf.length - 1]?.id ?? ''}:${
    laufend ? `${laufend.frage}:${laufend.antwort.length}` : ''
  }`
  useEffect(() => {
    const el = liste.current
    if (el) el.scrollTop = el.scrollHeight
  }, [stand])

  return (
    <main
      className="mx-auto flex h-[calc(100dvh_-_var(--buehne-leiste,0px))] max-w-md flex-col gap-3 bg-bg p-4"
      // So hoch wie das, was wirklich zu sehen ist: Mit offener Tastatur ist
      // das weniger als 100dvh, und die Eingabe bleibt über der Tastatur,
      // statt dass der Browser die Seite dorthin schiebt.
      style={hoehe ? { height: `calc(${hoehe}px - var(--buehne-leiste, 0px))` } : undefined}
    >
      <header className="flex items-center gap-3">
        <Avatar
          state={spricht ? 'speaking' : laufend ? 'thinking' : 'idle'}
          size={44}
          showLabel={false}
        />
        <p className="flex-1 text-base">
          {spricht ? 'K4ia antwortet am Bildschirm' : 'Frag K4ia — sie antwortet am Bildschirm'}
        </p>
      </header>
      {steuerung}

      <section
        ref={liste}
        aria-label="Gespräch"
        className="min-h-0 flex-1 space-y-3 overflow-y-auto"
      >
        {verlauf.map((n) => (
          <Blase key={n.id} rolle={n.role} text={n.content} foto={n.foto} partner={n.partner} />
        ))}
        {laufend && (
          <>
            <Blase rolle="user" text={laufend.frage} foto={laufend.foto} />
            <Blase rolle="assistant" text={laufend.antwort || '…'} />
          </>
        )}
        {foto.schaut && (
          <div className="ml-auto flex w-fit max-w-[85%] flex-col items-end gap-2">
            <img
              src={foto.schaut}
              alt="Dein Foto"
              className="max-h-48 rounded-[1.375rem] opacity-70"
            />
            <p className="text-sm text-text-muted" aria-live="polite">
              K4ia schaut sich das Foto an …
            </p>
          </div>
        )}
      </section>

      {chat.fehler && (
        <p className="text-center text-sm text-text-muted" role="alert">
          {chat.fehler}
        </p>
      )}
      {fehlerZeile}
      {foto.fehler && (
        <p className="text-center text-sm text-text-muted" role="alert">
          {foto.fehler}
        </p>
      )}
      {/* Sprechen und Foto nebeneinander über dem Eingabefeld — in einer Zeile mit
          dem Feld blieb dafür am Handy kaum Platz. Steht dort schon eine Frage,
          geht sie mit dem Foto. */}
      <div className="flex gap-2 [&>button]:flex-1">
        {mikrofonKnopf}
        <button
          type="button"
          onClick={foto.waehlen}
          disabled={!!laufend || !!foto.schaut}
          className="flex items-center justify-center gap-2 rounded-xl border border-border bg-bg-elevated px-4 py-3 font-semibold transition disabled:opacity-60"
        >
          <span aria-hidden>📷</span>
          Foto
        </button>
        {foto.feld(
          () => textRef.current.trim(),
          () => setText(''),
        )}
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (!text.trim() || laufend) return
          void chat.fragen(text.trim())
          setText('')
        }}
        className="flex gap-2"
      >
        <label htmlFor="intro-chat-input" className="sr-only">
          Deine Nachricht an K4ia
        </label>
        <input
          id="intro-chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Frage an K4ia …"
          className="min-w-0 flex-1 rounded-xl border border-border bg-bg-elevated px-4 py-3 outline-none focus:border-brand"
        />
        <button
          type="submit"
          disabled={!text.trim() || !!laufend}
          className="btn-primary rounded-xl px-4 py-3"
        >
          Senden
        </button>
      </form>
      <button onClick={onBoxen} className="rounded-xl border border-border px-4 py-3">
        Zurück zu den Boxen
      </button>
      <VorfuehrungBeenden {...(freigabe ?? { onEnde })} />
    </main>
  )
}

/**
 * Die Höhe des sichtbaren Ausschnitts (`visualViewport`): Öffnet sich am
 * Handy die Tastatur, schrumpft er — `100dvh` bleibt auf iOS und Android
 * Chrome dagegen, wie es war. `null`, solange es den Wert nicht gibt (alte
 * Browser, Tests); dann gilt die Klasse mit `100dvh`.
 */
function useSichtbareHoehe(): number | null {
  const [hoehe, setHoehe] = useState<number | null>(null)
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const lesen = () => setHoehe(Math.round(vv.height))
    lesen()
    vv.addEventListener('resize', lesen)
    return () => vv.removeEventListener('resize', lesen)
  }, [])
  return hoehe
}

function Blase({
  rolle,
  text,
  foto,
  partner,
}: {
  rolle: 'user' | 'assistant'
  text: string
  foto?: string
  partner?: IntroChatNachricht['partner']
}) {
  return rolle === 'user' ? (
    <div className="ml-auto flex w-fit max-w-[85%] flex-col items-end gap-2">
      {foto && <img src={foto} alt="Dein Foto" className="max-h-48 rounded-[1.375rem]" />}
      <p className="rounded-[1.375rem] rounded-br-md bg-accent px-4 py-3 text-on-accent">{text}</p>
    </div>
  ) : (
    <div className="markdown-body max-w-[85%] break-words rounded-[1.375rem] rounded-bl-md bg-surface px-4 py-3 text-[0.975rem] leading-relaxed text-text shadow-[var(--shadow-card)] [&_li]:ml-4 [&_li]:list-disc [&_p+p]:mt-2">
      <ReactMarkdown>{text}</ReactMarkdown>
      <PoweredBy showcase={partner} className="mt-2" />
    </div>
  )
}

/** Pause/Weiter und Stopp für das, was K4ia am Bildschirm gerade spricht. */
function Steuerung({
  pausiert,
  onBefehl,
}: {
  pausiert: boolean
  onBefehl: (art: 'pause' | 'weiter' | 'stopp') => void
}) {
  return (
    <div className="flex w-full gap-3" role="group" aria-label="Ansage steuern">
      <button
        onClick={() => onBefehl(pausiert ? 'weiter' : 'pause')}
        className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-border bg-bg-elevated px-4 py-3 font-semibold"
      >
        <span aria-hidden>{pausiert ? '▶' : '❚❚'}</span>
        {pausiert ? 'Weiter' : 'Pause'}
      </button>
      <button
        onClick={() => onBefehl('stopp')}
        className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-border bg-bg-elevated px-4 py-3 font-semibold"
      >
        <span aria-hidden>■</span>
        Stopp
      </button>
    </div>
  )
}

type AufnahmeStatus = 'bereit' | 'hoert' | 'versteht'

function MikrofonKnopf({ status, onClick }: { status: AufnahmeStatus; onClick: () => void }) {
  const hoert = status === 'hoert'
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={status === 'versteht'}
      aria-pressed={hoert}
      aria-label={hoert ? 'Fertig gesprochen' : 'Frage sprechen'}
      className={`flex shrink-0 items-center justify-center gap-2 rounded-xl px-4 py-3 font-semibold transition ${
        hoert ? 'bg-status-active text-white' : 'border border-border bg-bg-elevated'
      }`}
    >
      <span aria-hidden>{hoert ? '●' : '🎤'}</span>
      {status === 'hoert' ? 'Fertig' : status === 'versteht' ? '…' : 'Sprechen'}
    </button>
  )
}

/**
 * Nimmt zwischen `starten` und `stoppen` auf — ausdrücklich angetippt, wie im
 * Sprachmodus (FR-25): Im Lab steht Publikum daneben, ein offenes Mikrofon
 * nähme mit, was niemand an K4ia gerichtet hat.
 */
function useAufnahme() {
  const [status, setStatus] = useState<AufnahmeStatus>('bereit')
  const recorder = useRef<MediaRecorder | null>(null)
  const teile = useRef<Blob[]>([])
  const auszeit = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fertig = useRef<((blob: Blob | null) => void) | null>(null)

  async function starten() {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    // iOS kennt kein webm, dort nimmt MediaRecorder mp4.
    const typ = ['audio/webm', 'audio/mp4'].find((t) => MediaRecorder.isTypeSupported(t))
    const r = new MediaRecorder(stream, typ ? { mimeType: typ } : undefined)
    teile.current = []
    r.ondataavailable = (e) => {
      if (e.data.size > 0) teile.current.push(e.data)
    }
    r.onstop = () => {
      stream.getTracks().forEach((t) => t.stop())
      const blob = teile.current.length ? new Blob(teile.current, { type: r.mimeType }) : null
      fertig.current?.(blob)
      fertig.current = null
    }
    recorder.current = r
    r.start()
    setStatus('hoert')
    // Vergessen zu beenden: nach einer Weile von selbst aufhören, das Gesagte verfällt.
    auszeit.current = setTimeout(() => {
      void stoppen().then(() => setStatus('bereit'))
    }, AUFNAHME_MAX_MS)
  }

  function stoppen(): Promise<Blob | null> {
    if (auszeit.current) clearTimeout(auszeit.current)
    const r = recorder.current
    recorder.current = null
    if (!r || r.state === 'inactive') {
      setStatus('bereit')
      return Promise.resolve(null)
    }
    return new Promise((resolve) => {
      fertig.current = resolve
      r.stop()
    })
  }

  return { status, setStatus, starten, stoppen }
}

function Rahmen({
  avatar,
  children,
}: {
  avatar: 'idle' | 'thinking' | 'speaking' | 'error'
  children: ReactNode
}) {
  return (
    <main className="flex min-h-[calc(100dvh_-_var(--buehne-leiste,0px))] flex-col items-center justify-center gap-6 bg-bg p-8 text-center">
      <Avatar state={avatar} size={160} showLabel={false} />
      <div className="flex w-full max-w-sm flex-col items-center gap-4">{children}</div>
    </main>
  )
}
